<template>
  <Form
    v-if="resolutionStatusList && resolutionStatusList.length"
    ref="caseDetailsForm"
    :form-data="formData"
    :default-values="defaultValues"
    @onChange="updateOnChange"
    @updateOnSearch="updateOnSearch"
    @onSubmitForm="onSubmitForm"
    @onOpenDropdown="onOpenDropdown()"></Form>
</template>

<script>
import {
  disputeCaseDetailsForm,
  updateCaseDetailsPayloadGenerator,
  straitOfHormuzAllowedChargeCodes,
  straitOfHormuzChangeAllowedChargeCodes,
  chargeCodeValidationSkipReasonCodes,
} from "@/config/disputeUpdateModalConstant.js";
import {
  debounce,
  getMsalIdToken,
  resolutionDropdownMapping,
} from "@/utils/helpers";

// Configs
import {
  disputeStatusList,
  azureActiveDirectoryGroups,
  MANUAL_SURCHARGE_AUTO_RESOLUTION_ALGO_NAME,
  AUTO_RESOLUTION_OUTCOME_POTENTIAL_REJECT,
} from "@/config/constant.js";

// Actions
import { disputeActions, userActions } from "@/actions";

// Utilities
import AxiosFetch from "@/utils/axios";
import AxiosFetchForMsal from "@/utils/axiosForMsal";

export default {
  name: "DisputeCaseDetailsForm",
  props: {
    disputesConstants: {
      type: Object,
      required: true,
    },
    disputeLookupFields: {
      type: Object,
      required: true,
    },
    disputeChargecodeFields: {
      type: Object,
      required: true,
    },
    caseDetails: {
      type: Object,
      required: true,
    },
    formDisabledList: {
      type: Array,
      default: () => [],
    },
    formMandatoryList: {
      type: Array,
      default: () => [],
    },
    resolutionStatusList: {
      type: Array,
      required: true,
    },
    resolutionStagesList: {
      type: Array,
      required: true,
    },
    resolutionButtonValues: {
      type: Array,
      required: true,
    },
    selectedRowsLength: {
      type: Number,
      default: () => 0,
    },
    bulkData: {
      type: Array,
      default() {
        return [];
      },
    },
    nextStatusDropDownList: {
      type: Array,
      default: () => {},
    },
    updatedData: {
      type: Object,
      default: () => {},
    },
    isSLAMissed: {
      type: Boolean,
    },
    reasonForDelayOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      disputeStatusList: disputeStatusList,
      processorList: [],
      onStageDropdownChange: false,
      isPotentialReject: false,
      previousReasonCode: "",
      rephraseResponse: "",
      defaultValues: {},
      lookupFields: this.disputeLookupFields || {},
      chargeCodeFields: this.disputeChargecodeFields || {},
      dropdownSearchLoader: {
        ROOT_CAUSE: false,
        COORDINATOR: false,
        PROCESSOR_NAME: false,
        CREDIT_NOTE_FAILURE_REASON: false,
        CHARGE_CODE: false,
        RE_DISPUTE_ROOT_CAUSE: false,
        CURRENCY: false,
        localFormDisabledList: [...this.formDisabledList], //change to  add local form disable list
        localFormMandatoryList: [...this.formMandatoryList], //change to  add local form mandatory list
      },
      tier2OptionList: [
        {
          id: "TIER2",
          displayName: "TIER2: TIER2",
          label: "TIER2: TIER2",
          value: "TIER2: TIER2",
        },
        {
          id: "TCMTIER2",
          displayName: "TCMTIER2: TIER 2",
          label: "TCMTIER2: TIER 2",
          value: "TCMTIER2: TIER 2",
        },
      ],
      coordinatorStateValue: {},
      isIncorrectPayer: false,
      showCreditNote: false,
      showIncident: true,
      isChargeCodeApiFetched: false,
      isInvoiceApiCalled: false,
      invoiceCreditNote: "",
      invoiceChargeCodesFromApi: [],
      invoiceChargeCodesMap: {},
      isChargeCodeMandatoryForInProcess: false,
      isContingencyReasonMandatory: false,
      isContingencyReasonLocked: false,
      isContingencyReasonDisabled: false,
      isNAAutoSet: false,
      reviewDecisionValue: null,
      reviewReasonValue: null,
      // Dashboard search rows omit these fields; populated once fetchDisputeDetails fetches full case data
      autoResolutionAlgoNameValue:
        this.caseDetails?.autoResolutionAlgoName || null,
      autoResolutionOutcomeValue:
        this.caseDetails?.autoResolutionOutcome || null,
      commentResponse: "",
      commentsError: false,
      allRejectedByNull: false,
    };
  },
  computed: {
    coordinatorNotPresent() {
      return this.lookupFields.COORDINATOR.filter(
        (item) => item.id === this.caseDetails.coordinatorCode
      );
    },
    caseDetailsWithAutoResolution() {
      return {
        ...this.caseDetails,
        autoResolutionAlgoName: this.autoResolutionAlgoNameValue,
        autoResolutionOutcome: this.autoResolutionOutcomeValue,
      };
    },
    showFeedbackForm() {
      return (
        this.autoResolutionAlgoNameValue ===
          MANUAL_SURCHARGE_AUTO_RESOLUTION_ALGO_NAME &&
        this.autoResolutionOutcomeValue ===
          AUTO_RESOLUTION_OUTCOME_POTENTIAL_REJECT
      );
    },
    formData() {
      return disputeCaseDetailsForm(
        this.disputesConstants,
        this.resolutionStagesList,
        this.resolutionStatusList,
        this.getLookupFields(),
        this.chargeCodeFields,
        this.formDisabledList,
        this.formMandatoryList,
        this.dropdownSearchLoader,
        this.processorList,
        this.nextStatusDropDownList,
        this.onStageDropdownChange,
        this.caseDetailsWithAutoResolution,
        this.isPotentialReject,
        this.tier2OptionList,
        this.selectedRowsLength > 1 ? true : false,
        this.isSLAMissed,
        this.reasonForDelayOptions,
        this.isIncorrectPayer,
        this.showCreditNote,
        this.showIncident,
        this.invoiceCreditNote,
        this.isChargeCodeMandatoryForInProcess,
        this.isContingencyReasonMandatory,
        this.isContingencyReasonLocked,
        this.isContingencyReasonDisabled,
        this.reviewDecisionValue,
        this.reviewReasonValue
      );
    },
  },
  watch: {
    formDisabledList() {
      this.localFormDisabledList = this.formDisabledList; //changes to add to form disable list
    },
    formMandatoryList() {
      this.localFormMandatoryList = this.formMandatoryList;
    },
  },

  created() {
    // this.fetchDisputeComments();
    if (
      this.caseDetails.ohpDisputeId != "" &&
      this.selectedRowsLength > 1 == false
    ) {
      this.fetchDisputeDetails();
    }
    this.localFormDisabledList = this.formDisabledList; //changes to add to form disable list
    this.localFormMandatoryList = this.formMandatoryList;
    this.lookupFields = { ...this.disputeLookupFields };
    this.chargeCodeFields = { ...this.disputeChargecodeFields };
    const formFields = this.formData.map((el) => el.formFields);

    if (
      this.caseDetails &&
      Object.keys(this.caseDetails).length &&
      this.resolutionStagesList?.length
    ) {
      this.$emit("isSLAMissedCheck", {
        selectedDispute: this.bulkData.length
          ? this.bulkData
          : [this.caseDetails],
      });
      if (!this.selectedRowsLength || this.selectedRowsLength === 1) {
        [...formFields[0]]
          .filter((el) => el.type !== "title")
          .forEach((elem) => {
            if (elem.type === "select" || elem.type === "searchDropdown") {
              this.defaultValues[elem.name] = this.getDropdownValue(
                this.updatedData[elem.name] || this.caseDetails[elem.name],
                elem.options
              );
            } else {
              this.defaultValues[elem.name] =
                (typeof this.updatedData[elem.name] == "boolean"
                  ? this.updatedData[elem.name]
                  : this.updatedData[elem.name]?.toString()) ||
                (elem.name !== "noteDescription"
                  ? typeof this.caseDetails[elem.name] == "boolean"
                    ? this.caseDetails[elem.name]
                    : this.caseDetails[elem.name]?.toString()
                  : null);
            }
          });

        //changes done for processor field change
        if (this.caseDetails["reasonCode"]) {
          this.previousReasonCode = this.caseDetails["reasonCode"];
        }

        const chargeCodeValues = [];
        (this.updatedData["chargeCode"] || this.caseDetails["chargeCode"] || "")
          .split("+")
          ?.forEach((key) => {
            const itemIndex =
              this.disputeChargecodeFields?.CHARGE_CODE?.findIndex(
                (listItem) => listItem?.id === key.toString()
              );
            itemIndex > -1 &&
              chargeCodeValues.push(
                this.disputeChargecodeFields?.CHARGE_CODE[itemIndex]
              );
          });
        this.defaultValues["chargeCode"] = chargeCodeValues;

        // Set reactive values for dropdown options; actual form state set after mount in fetchDisputeDetails
        const feedback = this.caseDetails?.disputeResolutionFeedback;
        if (feedback) {
          this.reviewDecisionValue = feedback.reviewDecision || null;
          this.reviewReasonValue = feedback.reviewReason || null;
          this.defaultValues["reviewJustification"] =
            feedback.reviewJustification || null;
        }

        this.search(
          this,
          {
            searchValue:
              this.updatedData?.processorName ||
              this.caseDetails?.processorName,
          },
          { name: "processorName" },
          true
        );
      } else {
        const disputeStageField = [...formFields[0]].filter(
          (el) => el.name === "disputeStage"
        )[0];
        this.defaultValues["disputeStage"] = this.getDropdownValue(
          this.updatedData["disputeStage"] || this.caseDetails["disputeStage"],
          disputeStageField.options
        );
      }
    }
  },
  methods: {
    getLookupFields() {
      if (
        this.caseDetails.coordinatorFunction &&
        this.caseDetails.coordinatorCode &&
        this.caseDetails.coordinatorCode !== "<BLANK>" &&
        !this.coordinatorNotPresent.length
      ) {
        this.lookupFields.COORDINATOR = [
          ...this.lookupFields.COORDINATOR,
          {
            id: this.caseDetails.coordinatorCode,
            displayName: this.caseDetails.coordinatorFunction,
            label: this.caseDetails.coordinatorFunction,
            value: this.caseDetails.coordinatorFunction,
          },
        ];
      }
      return this.lookupFields;
    },
    updateChargeCodeMandatory(formState) {
      const nonMandatoryReasonCodes = [
        "0002",
        "0005",
        "0006",
        "0010",
        "0011",
        "0012",
        "0013",
        "0014",
      ];
      this.isChargeCodeMandatoryForInProcess =
        !formState["chargeCode"]?.length &&
        !nonMandatoryReasonCodes.includes(formState["reasonCode"]?.id);
    },
    updateContingencyReasonMandatory(formState) {
      const nonMandatoryReasonCodes = [
        "0002",
        "0006",
        "0010",
        "0011",
        "0012",
        "0013",
        "0014",
      ];

      const selectedChargeCodes = (formState["chargeCode"] || [])
        .map((item) => (item?.id || item)?.split("-")[0])
        .filter(Boolean);

      const hasAllowedChargeCode = selectedChargeCodes.some((code) =>
        straitOfHormuzAllowedChargeCodes.includes(code)
      );

      // If all selected charges are outside the allowed list, disable and default to N/A
      // But preserve existing "Strait of Hormuz" value only if user hasn't changed it
      if (selectedChargeCodes.length > 0 && !hasAllowedChargeCode) {
        this.isContingencyReasonMandatory = false;
        if (
          this.caseDetails?.contingencyReason === "Strait of Hormuz" &&
          formState["contingencyReason"]?.value === "Strait of Hormuz"
        ) {
          this.isContingencyReasonDisabled = false;
        } else {
          this.isContingencyReasonDisabled = true;
          this.isNAAutoSet = true;
          if (formState["contingencyReason"]?.value !== "N/A") {
            this.$nextTick(() => {
              this.$refs.caseDetailsForm.updateFormState("contingencyReason", {
                id: "N/A",
                label: "N/A",
                value: "N/A",
              });
            });
          }
        }
      } else if (hasAllowedChargeCode) {
        // At least one selected charge is in the allowed list - enable dropdown
        this.isContingencyReasonDisabled = false;
        this.isContingencyReasonMandatory =
          formState["statusCode"]?.id === "20" &&
          !nonMandatoryReasonCodes.includes(formState["reasonCode"]?.id);
        // Clear N/A only if it was auto-set by the system, not user-selected
        if (
          this.isNAAutoSet &&
          formState["contingencyReason"]?.value === "N/A"
        ) {
          this.isNAAutoSet = false;
          this.$nextTick(() => {
            this.$refs.caseDetailsForm.updateFormState("contingencyReason", {});
          });
        }
      } else {
        // No charge codes selected - disable contingency reason
        this.isContingencyReasonDisabled = true;
        this.isContingencyReasonMandatory = false;
      }
    },
    updateContingencyReasonLocked(formState) {
      if (this.selectedRowsLength > 1) {
        this.isContingencyReasonLocked = false;
        return;
      }
      if (
        this.caseDetails?.contingencyReason === "Strait of Hormuz" &&
        formState["contingencyReason"]?.value === "Strait of Hormuz"
      ) {
        const selectedChargeCodes = (formState["chargeCode"] || [])
          .map((item) => (item?.id || item)?.split("-")[0])
          .filter(Boolean);
        this.isContingencyReasonLocked = !selectedChargeCodes.some((code) =>
          straitOfHormuzChangeAllowedChargeCodes.includes(code)
        );
      } else {
        this.isContingencyReasonLocked = false;
      }
    },
    async fetchDisputeDetails() {
      try {
        const { data } = await AxiosFetchForMsal(
          disputeActions.getDisputeDetailsById(
            this.caseDetails.ohpDisputeId,
            `Bearer ${await getMsalIdToken(this)}`
          )
        );
        // this.caseDetails = data;
        this.autoResolutionAlgoNameValue = data?.autoResolutionAlgoName || null;
        this.autoResolutionOutcomeValue = data?.autoResolutionOutcome || null;
        this.creditNoteDashboard = data?.creditNote || "";
        this.incidentIdDashboard = data?.incidentId || "";
        this.failureReasonDashboard = data?.failureReason || "";
        this.isCreditNoteManualDashboard = data?.isCreditNoteManual || false;
        this.$refs.caseDetailsForm.updateFormState(
          "isCreditNoteManual",
          data?.isCreditNoteManual || false
        );
        this.$refs.caseDetailsForm.updateFormState(
          "creditNote",
          data?.creditNote || ""
        );
        this.$refs.caseDetailsForm.updateFormState(
          "incidentId",
          data?.incidentId || ""
        );
        this.$refs.caseDetailsForm.updateFormState("failureReason", {
          label: data?.failureReason || "",
          value: data?.failureReason || "",
        });

        if (this.isCreditNoteManualDashboard) {
          this.showCreditNote = true;
          this.showIncident = false;
        }

        const feedback = data?.disputeResolutionFeedback;
        if (feedback) {
          // Pre-set reactive values so dependent dropdowns have correct options before form state is applied
          this.reviewDecisionValue = feedback.reviewDecision || null;
          this.reviewReasonValue = feedback.reviewReason || null;

          this.$refs.caseDetailsForm.updateFormState(
            "reviewDecision",
            feedback.reviewDecision
              ? {
                  id: feedback.reviewDecision,
                  label: feedback.reviewDecision,
                  value: feedback.reviewDecision,
                }
              : {}
          );

          // Wait for the $nextTick clear queued by updateOnChange(reviewDecision) to finish before setting reviewReason
          await this.$nextTick();
          this.reviewDecisionValue = feedback.reviewDecision || null;
          this.reviewReasonValue = feedback.reviewReason || null;

          this.$refs.caseDetailsForm.updateFormState(
            "reviewReason",
            feedback.reviewReason
              ? {
                  id: feedback.reviewReason,
                  label: feedback.reviewReason,
                  value: feedback.reviewReason,
                }
              : {}
          );

          await this.$nextTick();

          this.$refs.caseDetailsForm.updateFormState(
            "reviewSubReason",
            feedback.reviewSubReason
              ? {
                  id: feedback.reviewSubReason,
                  label: feedback.reviewSubReason,
                  value: feedback.reviewSubReason,
                }
              : {}
          );

          this.$refs.caseDetailsForm.updateFormState(
            "reviewJustification",
            feedback.reviewJustification || ""
          );
        }
      } catch (error) {
        console.error(error);
      }
    },
    getDropdownValue(id, list) {
      const itemIndex = list?.findIndex(
        (listItem) => listItem.id === id || listItem.value === id
      );
      if (itemIndex > -1) {
        return list[itemIndex];
      } else {
        return {};
      }
    },
    async updateOnChange(currentDiff, diffWithDefaultValues, formState) {
      if (
        Object.prototype.hasOwnProperty.call(
          currentDiff || {},
          "reviewDecision"
        )
      ) {
        this.reviewDecisionValue = currentDiff["reviewDecision"]?.value || null;
        this.reviewReasonValue = null;
        this.$nextTick(() => {
          this.$refs.caseDetailsForm?.updateFormState("reviewReason", {});
          this.$refs.caseDetailsForm?.updateFormState("reviewSubReason", {});
        });
      }
      if (
        Object.prototype.hasOwnProperty.call(currentDiff || {}, "reviewReason")
      ) {
        this.reviewReasonValue = currentDiff["reviewReason"]?.value || null;
        this.$nextTick(() => {
          this.$refs.caseDetailsForm?.updateFormState("reviewSubReason", {});
        });
      }
      if (formState["incidentId"]?.length > 0) {
        this.showIncident = true;
      } else {
        this.showIncident = false;
      }
      // Make contingencyReason mandatory for status 20 and specific reason codes
      this.updateContingencyReasonMandatory(formState);
      // Lock contingency reason if already Strait of Hormuz unless charge code is ECS or PSS
      this.updateContingencyReasonLocked(formState);

      // Handle charge code API for non-credit-note statuses
      if (
        formState["statusCode"]?.id !== "61" &&
        formState["statusCode"]?.id !== "63" &&
        this.isChargeCodeApiFetched === false
      ) {
        // For bulk GAMs, pass all selected invoice numbers
        const invoiceNumbers =
          this.selectedRowsLength > 1
            ? this.bulkData.map((item) => item.invoiceNumber).join(",")
            : this.caseDetails.invoiceNumber;
        this.$emit("invoiceApiLoading", true);
        this.isChargeCodeApiFetched = true;
        this.isInvoiceApiCalled = false; // Reset credit note flag when fetching charge codes
        try {
          const { data } = await AxiosFetchForMsal(
            disputeActions.getDisputeInvoiceDetailsFromSap(
              await getMsalIdToken(this),
              invoiceNumbers
            )
          );
          // Store charge codes from API response for validation on submit
          if (data && data.invoiceList) {
            // build invoice -> chargeCode mapping for bulk validation
            const map = {};
            data.invoiceList.forEach((item) => {
              const inv = item.invoiceNumber || "";
              const code = item.chargeCode;
              if (!map[inv]) map[inv] = [];
              if (!map[inv].includes(code)) map[inv].push(code);
            });
            this.invoiceChargeCodesMap = map;
            this.invoiceChargeCodesFromApi = [
              ...new Set(data.invoiceList.map((item) => item.chargeCode)),
            ];
            this.updateChargeCodeMandatory(formState);
          }
        } finally {
          this.$emit("invoiceApiLoading", false);
        }
      } else if (
        formState["statusCode"]?.id !== "61" &&
        formState["statusCode"]?.id !== "63" &&
        this.isChargeCodeApiFetched
      ) {
        this.updateChargeCodeMandatory(formState);
      }
      // Handle credit note API for statuses 61/63
      else if (
        (formState["statusCode"]?.id === "61" ||
          formState["statusCode"]?.id === "63") &&
        this.isInvoiceApiCalled === false &&
        this.selectedRowsLength > 1 == false
      ) {
        const { data } = await AxiosFetchForMsal(
          disputeActions.getDisputeInvoiceDetailsFromSap(
            await getMsalIdToken(this),
            this.caseDetails.invoiceNumber
          )
        );
        this.isInvoiceApiCalled = true;
        this.isChargeCodeApiFetched = false; // Reset charge code flag when fetching credit note
        console.log("data", data);
        if (data && data.invoiceList) {
          this.invoiceCreditNote = data?.invoiceList[0]?.cancelledInvoiceNo;
          this.$refs.caseDetailsForm.updateFormState(
            "creditNote",
            this.invoiceCreditNote != ""
              ? this.invoiceCreditNote
              : this.creditNoteDashboard
          );
        }
        console.log("invoiceCreditNote", this.invoiceCreditNote);
        if (
          this.invoiceCreditNote === "" ||
          this.invoiceCreditNote === undefined
        ) {
          this.showCreditNote = true;
          this.$refs.caseDetailsForm.updateFormState(
            "isCreditNoteManual",
            true
          );
          this.showIncident = false;
        } else {
          this.showCreditNote = true;
          this.$refs.caseDetailsForm.updateFormState(
            "isCreditNoteManual",
            false
          );
          //   this.showIncident = true;
        }
      } else if (
        (formState["statusCode"]?.id === "61" ||
          formState["statusCode"]?.id === "63") &&
        this.isInvoiceApiCalled === true &&
        this.selectedRowsLength > 1 == false
      ) {
        this.showCreditNote = true;
        this.showIncident = false;
      } else if (
        formState["statusCode"]?.id !== "61" &&
        formState["statusCode"]?.id !== "63" &&
        this.selectedRowsLength > 1 == false
      ) {
        this.isInvoiceApiCalled = false;
        this.$refs.caseDetailsForm.updateFormState("isCreditNoteManual", false);
        this.showCreditNote = false;
        this.showIncident = true;
      }

      if (currentDiff["isCreditNoteManual"] == true && this.showCreditNote) {
        // this.$refs.caseDetailsForm.updateFormState("isCreditNoteManual", true);
        // this.showCreditNote = true;
        this.showIncident = false;
        this.showCreditNote = true;
      } else if (
        currentDiff["isCreditNoteManual"] == false &&
        !this.showCreditNote
      ) {
        this.showIncident = true;
        this.showCreditNote = false;
        // this.$refs.caseDetailsForm.updateFormState("isCreditNoteManual", false);
        // this.showCreditNote = false;
      }

      if (formState["disputeStage"]?.value !== "Accept") {
        this.showCreditNote = false;
      }

      if (
        Object.prototype.hasOwnProperty.call(currentDiff || {}, "disputeStage")
      ) {
        this.$refs.caseDetailsForm.updateFormState("statusCode", {});
        this.$emit("updateDisputeStage", currentDiff["disputeStage"]?.value);
        this.$emit(
          "getCaseDetailsStageStatusList",
          this.caseDetails,
          currentDiff["disputeStage"]?.label
        );
        this.isPotentialReject &&
          this.$refs.caseDetailsForm?.updateFormState(
            "coordinatorCode",
            this.coordinatorStateValue
          );

        this.isPotentialReject = false;
        this.onStageDropdownChange = true;
        if (
          currentDiff["disputeStage"]?.value === "Reject" &&
          this.caseDetails.disputeStage !== currentDiff["disputeStage"]?.value
        ) {
          this.$refs.caseDetailsForm.updateFormState("resolutionDelayReason", {
            id: "select",
            label: "Select",
            value: "",
          });
        } else {
          this.$refs.caseDetailsForm.updateFormState("resolutionDelayReason", {
            id: this.caseDetails?.resolutionDelayReason || "select",
            label: this.caseDetails?.resolutionDelayReason || "Select",
            value: this.caseDetails?.resolutionDelayReason || "",
          });
        }
      } else if (
        Object.prototype.hasOwnProperty.call(currentDiff || {}, "statusCode")
      ) {
        if (formState?.["disputeStage"]?.value === "Reject with voice") {
          this.isPotentialReject = true;
          this.coordinatorStateValue = formState?.["coordinatorCode"];
          this.$refs.caseDetailsForm?.updateFormState(
            "coordinatorCode",
            this?.caseDetails?.isTender
              ? this.tier2OptionList?.[1]
              : this.tier2OptionList?.[0]
          );
        }

        if (currentDiff["statusCode"]?.id !== "select") {
          this.$emit("updateDisputeStatus", currentDiff["statusCode"]?.id);
          this.$emit(
            "getCaseDetailsRulesList",
            this.caseDetails,
            currentDiff["statusCode"]?.id
          );
          this.onStageDropdownChange = false;
        }
      }

      if (currentDiff["rephraseSentence"]) {
        const currentComment = formState?.noteDescription.replace(
          /<\/?[^>]+(>|$)/g,
          ""
        );
        let rephraseCommentRequest = {
          phrase: currentComment,
          language:
            formState?.languageList &&
            Object.keys(formState.languageList).length > 0
              ? formState.languageList
              : "English",
        };
        const rephrasedResponse = await this.rephraseComment(
          rephraseCommentRequest
        );
        if (rephrasedResponse) {
          //console.log("response from api : " + this.rephraseResponse);
          this.$refs.caseDetailsForm.updateFormState(
            "rephrasedText",
            this.rephraseResponse
          );
          // console.log("ref " + this.$refs.caseDetailsForm.$children[16].$children[0].$refs.editor);
          // this.$refs.caseDetailsForm.$children[16].$children[0].$refs.editor.clipboard.dangerouslyPasteHTML('<p></p>');
        }
      }

      if (
        formState["reasonCode"]?.id === "0002" &&
        this.isIncorrectPayer !== true
      ) {
        this.isIncorrectPayer = true;
        if (
          this.caseDetails?.isRedisputed === true &&
          Object.keys(diffWithDefaultValues).length !== 0
        ) {
          this.$refs.caseDetailsForm.updateFormState("redisputeRootCauseCode", {
            id: "select",
            label: "Select",
            value: "",
          });
          this.$refs.caseDetailsForm.updateFormState("redisputeOutcomeCode", {
            id: "select",
            label: "Select",
            value: "",
          });
          this.$refs.caseDetailsForm.updateFormState("redisputeCategory", {
            id: "select",
            label: "Select",
            value: "",
          });
        } else if (Object.keys(diffWithDefaultValues).length !== 0) {
          this.$refs.caseDetailsForm.updateFormState("rootCauseCode", {
            id: "select",
            label: "Select",
            value: "",
          });
          this.$refs.caseDetailsForm.updateFormState("disputeOutcomeCode", {
            id: "select",
            label: "Select",
            value: "",
          });
          this.$refs.caseDetailsForm.updateFormState("categoryCode", {
            id: "select",
            label: "Select",
            value: "",
          });
        }
      } else if (formState["reasonCode"]?.id === "0002") {
        this.isIncorrectPayer = true;
      } else {
        this.isIncorrectPayer = false;
      }

      const currentDiffReasonCode = currentDiff["reasonCode"]?.id || null;
      if (
        this.previousReasonCode === currentDiffReasonCode ||
        currentDiffReasonCode === null
      ) {
        return;
      }

      const isTransitionToReasonCode =
        (currentDiffReasonCode === "0002" ||
          currentDiffReasonCode === "0010") &&
        this.previousReasonCode !== currentDiffReasonCode &&
        !["0002", "0010"].includes(this.previousReasonCode);

      // Check if "processorName" is in the mandatory list
      const isProcessorNameMandatory =
        this.localFormMandatoryList.includes("processorName");

      if (
        (isTransitionToReasonCode || isTransitionFromReasonCode) &&
        !isProcessorNameMandatory
      ) {
        //console.log("form mandatory list: " + this.localFormMandatoryList);

        this.$refs.caseDetailsForm.updateFormState("processorName", {
          value: "",
        });

        if (
          Array.isArray(this.localFormDisabledList) &&
          !this.localFormDisabledList.includes("processorName")
        ) {
          this.localFormDisabledList.push("processorName");
        }
      } else if (!isProcessorNameMandatory) {
        const index = this.localFormDisabledList.indexOf("processorName");
        if (index > -1) {
          this.localFormDisabledList.splice(index, 1);
        }
      }

      if (currentDiffReasonCode != null) {
        this.previousReasonCode = currentDiffReasonCode;
      }
    },
    updateOnSearch(searchTerm, field) {
      if (searchTerm.length > 2) {
        this.search(this, searchTerm, field);
        this.dropdownSearchLoader[field?.optionName] = true;
      } else {
        this.lookupFields = { ...this.disputeLookupFields };
        this.chargeCodeFields = { ...this.disputeChargecodeFields };
      }
    },
    search: debounce(async (that, searchTerm, field, calledOnMount) => {
      try {
        const fieldName =
          field.optionName === "RE_DISPUTE_ROOT_CAUSE"
            ? "ROOT_CAUSE"
            : field.optionName;
        if (field.name === "processorName") {
          const opts = userActions.getUserListFromAzureAdByEmployeeId(
            searchTerm.searchValue,
            azureActiveDirectoryGroups.azureAdForAssigneeDisputeAccess,
            `Bearer ${await getMsalIdToken(that)}`
          );
          const { data } = await AxiosFetch(opts);
          let users = data.users.map((item) => ({
            ...item,
            value: item.userName,
          }));
          that.processorList = users;
          if (calledOnMount && that.caseDetails.processorName) {
            const user = users.filter(
              (user) => user.apmId === that.caseDetails.processorName
            );
            that.$refs.caseDetailsForm.updateFormState(
              "processorName",
              user[0]
            );
          }
        } else if (field.name === "chargeCode") {
          const opts = disputeActions.getChargeCodeDropdownValues(
            await getMsalIdToken(that),
            searchTerm.searchValue
          );
          const { data } = await AxiosFetchForMsal(opts);
          const chargeCodeFields = resolutionDropdownMapping(
            data[fieldName].results,
            fieldName
          );
          that.chargeCodeFields[fieldName] = [...chargeCodeFields];
        } else {
          const opts = disputeActions.disputeFieldLookups(
            await getMsalIdToken(that),
            fieldName,
            searchTerm.searchValue
          );

          const { data } = await AxiosFetchForMsal(opts);
          const disputeLookupFields = resolutionDropdownMapping(
            data[fieldName].results,
            fieldName
          );
          that.lookupFields[fieldName] = [...disputeLookupFields];
        }
      } catch (err) {
        console.log(err);
      }
      that.dropdownSearchLoader[field?.optionName] = false;
    }, 350),
    onOpenDropdown() {
      this.lookupFields = { ...this.disputeLookupFields };
      this.chargeCodeFields = { ...this.disputeChargecodeFields };
    },
    onSubmitCalled() {
      this.$refs.caseDetailsForm.validate();
    },
    onSubmitForm(formState, changedFields) {
      this.allRejectedByNull = false;

      // Validate charge codes against invoice for all cases (not just status 20)
      const currentReasonCode = formState["reasonCode"]?.id || "";
      const skipChargeCodeValidation =
        chargeCodeValidationSkipReasonCodes.includes(currentReasonCode);
      const selectedChargeCodes = (formState["chargeCode"] || [])
        .map((item) => (item?.id || item)?.split("-")[0])
        .filter(Boolean);
      const validationErrors = [];

      if (!skipChargeCodeValidation && selectedChargeCodes.length > 0) {
        if (this.selectedRowsLength > 1) {
          const uniqueInvoices = [
            ...new Set(this.bulkData.map((d) => d.invoiceNumber)),
          ];
          const chargeSets = uniqueInvoices.map(
            (inv) =>
              new Set(
                (this.invoiceChargeCodesMap[inv] || []).map(
                  (c) => (c || "").split("-")[0]
                )
              )
          );
          const common = chargeSets.reduce(
            (a, b) => new Set([...a].filter((x) => b.has(x)))
          );
          if (!selectedChargeCodes.every((code) => common.has(code))) {
            validationErrors.push(
              "Bulk update is possible only if disputed charge code is the same across all selected invoices"
            );
          }
        } else if (
          this.invoiceChargeCodesFromApi.length > 0 &&
          !this.invoiceChargeCodesFromApi.every((code) => !isNaN(code)) //added this condition to skip the charge code which are not purely numeric as those are not coming from SAP(material number) and are added manually by agents
        ) {
          const invalidChargeCodes = selectedChargeCodes.filter(
            (code) => !this.invoiceChargeCodesFromApi.includes(code)
          );
          if (invalidChargeCodes.length > 0) {
            validationErrors.push(
              `Selected charge code(s) ${invalidChargeCodes.join(
                ", "
              )} does not match invoice charge codes`
            );
          }
        }
      }

      // Status 20: Validate Strait of Hormuz contingency reason against allowed charge codes
      if (formState["statusCode"]?.id === "20") {
        const isContingencyPrePopulated =
          this.caseDetails?.contingencyReason === "Strait of Hormuz"; // removed the prepopulated charge code check as it was affecting MyFin disputed cases
        if (
          !skipChargeCodeValidation &&
          !isContingencyPrePopulated &&
          formState["contingencyReason"]?.value === "Strait of Hormuz"
        ) {
          const hasAllowedCharge = selectedChargeCodes.some((code) =>
            straitOfHormuzAllowedChargeCodes.includes(code)
          );
          if (!hasAllowedCharge) {
            validationErrors.push(
              "Incorrect contingency reason selected for the disputed charge code"
            );
          }
        }
      }

      if (validationErrors.length > 0) {
        validationErrors.forEach((msg) => this.$emit("onBulkUpdateError", msg));
        return;
      }

      if (
        formState["disputeStage"]?.value === "Reject" &&
        this.caseDetails.disputeStage !== formState["disputeStage"]?.value &&
        "resolutionDelayReason" in formState
      ) {
        changedFields = {
          ...changedFields,
          resolutionDelayReason: formState.resolutionDelayReason,
        };
      }
      let updateCaseDetailPayload = {};
      if (this.selectedRowsLength > 1) {
        if (
          (formState?.statusCode?.id === "61" ||
            formState?.statusCode?.id === "63") &&
          this.selectedRowsLength > 100
        ) {
          this.$emit(
            "onBulkUpdateError",
            "Bulk disputes update limit is 100 for 61 & 63 Accepted cases"
          );
          return;
        }
        if (
          formState?.statusCode?.id === "61" ||
          formState?.statusCode?.id === "63"
        ) {
          formState.isBulkUpdateCreditNote = true;
        } else {
          formState.isBulkUpdateCreditNote = false;
        }
        const updatedData = updateCaseDetailsPayloadGenerator(
          formState,
          false,
          this.showFeedbackForm
        );
        this.generatePayload(
          updateCaseDetailPayload,
          updatedData,
          updatedData,
          "bulk"
        );
      } else {
        changedFields = {
          ...changedFields,
          failureReason: formState?.failureReason,
        };
        // Only include contingencyReason if it actually changed
        if (
          formState?.contingencyReason?.value !==
          this.caseDetails?.contingencyReason
        ) {
          changedFields.contingencyReason = formState?.contingencyReason;
        }
        // Include noteDescription if it has a value but wasn't changed (e.g., prepopulated default)
        if (!changedFields.noteDescription && formState?.noteDescription) {
          changedFields.noteDescription = formState.noteDescription;
        }
        // Include reviewJustification if it has a value but wasn't changed (e.g., prepopulated from feedback)
        if (
          this.showFeedbackForm &&
          !changedFields.reviewJustification &&
          formState?.reviewJustification
        ) {
          changedFields.reviewJustification = formState.reviewJustification;
        }
        const updatedData = updateCaseDetailsPayloadGenerator(
          changedFields,
          this.showIncident,
          this.showFeedbackForm
        );
        this.generatePayload(
          updateCaseDetailPayload,
          updatedData,
          changedFields,
          "single"
        );
        // generatePayload flattens changedFields keys and misses the nested feedback object
        delete updateCaseDetailPayload["reviewDecision"];
        delete updateCaseDetailPayload["reviewReason"];
        delete updateCaseDetailPayload["reviewSubReason"];
        delete updateCaseDetailPayload["reviewJustification"];
        if (this.showFeedbackForm) {
          updateCaseDetailPayload.disputeResolutionFeedback =
            updatedData.disputeResolutionFeedback;
        }

        const isCreditNoteStatus = ["61", "63"].includes(
          formState?.statusCode?.id
        );
        if (
          this.showCreditNote &&
          (isCreditNoteStatus || this.caseDetails?.isRedisputed)
        ) {
          updateCaseDetailPayload["creditNote"] = formState?.creditNote || null;
        }

        if (this.caseDetails?.processorName === updatedData.processorName) {
          delete updateCaseDetailPayload["processorName"];
          delete updateCaseDetailPayload["processorFullName"];
        } else if (
          updateCaseDetailPayload["processorName"] &&
          !updateCaseDetailPayload["processorFullName"]
        ) {
          // Ensure processorFullName is included when processorName is being updated
          updateCaseDetailPayload["processorFullName"] =
            changedFields["processorName"]?.userName;
        }

        // Always include processorFullName even when updating other fields
        // Get it from changed fields if processor was updated, otherwise from current form state
        if (!updateCaseDetailPayload["processorFullName"]) {
          const currentProcessorName = formState["processorName"];
          if (currentProcessorName && currentProcessorName.userName) {
            updateCaseDetailPayload["processorFullName"] =
              currentProcessorName.userName;
          }
        }
      }
      delete updateCaseDetailPayload["isExternalComment"];
      this.$emit("onSubmit", formState, updateCaseDetailPayload);
    },
    generatePayload(updateCaseDetailPayload, updatedData, iterator, from) {
      Object.keys(iterator).forEach((item) => {
        // Ignore malformed keys produced by non-input fields in form diff
        if (!item || item === "undefined") return;

        if (updatedData[item] && typeof updatedData[item] === "object") {
          Object.keys(updatedData[item]).forEach((elem) => {
            if (!elem || elem === "undefined") return;

            if (
              updatedData[item][elem] === "select" ||
              !updatedData[item][elem]
            ) {
              updateCaseDetailPayload[elem] =
                from === "bulk" ? undefined : null;
            } else {
              updateCaseDetailPayload[elem] = updatedData[item][elem];
            }
          });
        } else {
          if (updatedData[item] === "select" || !updatedData[item]) {
            updateCaseDetailPayload[item] = from === "bulk" ? undefined : null;
          } else {
            updateCaseDetailPayload[item] = updatedData[item];
          }
        }
      });
    },
    async rephraseComment(payload) {
      //console.log("Payload is : " + payload);
      try {
        const rephraseResponse = await AxiosFetchForMsal(
          await disputeActions.rephraseComment(
            payload,
            `Bearer ${await getMsalIdToken(this)}`
          )
        );
        this.rephraseResponse = rephraseResponse.data.gpt_repharsed_text;
        if (rephraseResponse.status == 200) {
          return true;
        }
      } catch (err) {
        console.log(err);
      }
    },
  },
};
</script>